import { useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { useTranslation } from '../../../contexts/TranslationContext';
import { selectedDeviceStateAtom } from '../../../models/atoms/device_atoms';
import { DeviceImageUpload } from './DeviceImageUpload';
import { DeviceInfoSection } from './DeviceInfoSection';
import { DisclaimerText } from '../MHM/MHMTypography';
import { Stack, styled, useTheme } from '@mui/material';
import { MHMButton } from '../MHM/MHMButton';
import JSZip from 'jszip';

export function formatImageURL(image: string) {
  if (!image) {
    return '';
  }

  return `${import.meta.env.REACT_APP_DEVICE_IMAGE_CDN_URL}/${image}`;
}

export function DeviceImages({ canEdit }: { canEdit: boolean }) {
  const { t } = useTranslation();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const selectedDevice = useRecoilValue(selectedDeviceStateAtom);
  const { palette } = useTheme();
  const images = selectedDevice?.images ?? [];
  const deviceId = selectedDevice?.device_id ?? '';

  const ListImage = styled('img')({
    'border': '2px solid transparent',
    'borderRadius': 5,
    'cursor': 'pointer',
    'maxWidth': '20%',
    '&:hover': { borderColor: palette.primary.main },
  });

  useEffect(() => {
    if (!selectedDevice || images.length === 0) {
      return;
    }

    setCurrentImageIndex(0);
  }, [images.length, selectedDevice, setCurrentImageIndex]);

  if (selectedDevice === null) {
    return null;
  }

  function downloadImages() {
    if (!images || !images.length) {
      return;
    }

    const zip = new JSZip();
    const folder = zip.folder(`installation_photos_${deviceId}`);

    Promise.all(
      images.map(async (image, index) => {
        const imgURL = formatImageURL(image);
        const response = await fetch(imgURL);
        const blob = await response.blob();
        folder?.file(`photo_${index + 1}.jpg`, blob);
      }),
    ).then(() => {
      zip.generateAsync({ type: 'blob' }).then((content: Blob) => {
        const link = document.createElement('a');
        link.href = URL.createObjectURL(content);
        link.download = `installation_photos_${deviceId}.zip`;
        link.click();
      });
    });
  }

  return (
    <>
      <DeviceImageUpload deviceID={deviceId} canEdit={canEdit} />
      <MHMButton onClick={downloadImages} disabled={!images.length}>
        {t('Download Images')}
      </MHMButton>
      <DeviceInfoSection.Entry label={t('Installation Photos:')} gap={1}>
        {images.length ? (
          <>
            <Stack direction="row" gap={0.125} overflow="auto">
              {images.map((image, index) => {
                const imgURL = formatImageURL(image);
                return (
                  <ListImage
                    className="installation-photo"
                    alt="installation"
                    key={imgURL}
                    src={imgURL}
                    onClick={() => setCurrentImageIndex(index)}
                  />
                );
              })}
            </Stack>
            <Stack justifyContent="center" alignItems="center">
              <img
                alt="selected installation"
                style={{
                  maxWidth: '70%',
                  borderRadius: 5,
                }}
                src={formatImageURL(images[currentImageIndex])}
              />
            </Stack>
          </>
        ) : (
          <DisclaimerText>{t('(No Photos)')}</DisclaimerText>
        )}
      </DeviceInfoSection.Entry>
    </>
  );
}
